import { useQuery } from '@tanstack/react-query'
import { doc, getDoc } from 'firebase/firestore'
import { db } from '@/core/firebase/client'
import { logger } from '@/core/logger'
import { APP_VERSION } from './version'

/**
 * Result of comparing the running build against `platform_config/app_versions`.
 *
 * `VersionCheckResult` so the two clients share one
 * Firestore document and behave the same way:
 * - `hasUpdate`  → newer version exists (user should update but is not forced)
 * - `isForced`   → current version is below `minRequired` (no dismiss)
 */
export interface VersionCheckResult {
  currentVersion: string
  latestVersion: string
  minRequired: string
  releaseDate: string
  hasUpdate: boolean
  isForced: boolean
}

const PLATFORM_CONFIG = 'platform_config'
const APP_VERSIONS_DOC = 'app_versions'
/**
 * Shared with the app: both clients read `platform_config/app_versions
 * /clubmanager`. The doc must track the React PWA's version lifecycle — the
 * build's higher version number means its own check will always
 * report `hasUpdate=false` regardless of what we publish here.
 */
const APP_KEY = 'clubmanager'

/**
 * Re-check the published version every 15 minutes so a tab left open
 * overnight still catches a release cut the next morning. The dialog
 * handles its own "dismissed this session" state — refetches that hit
 * the same latest version are no-ops for the user.
 */
const REFETCH_INTERVAL_MS = 15 * 60_000

export function useVersionCheck(): VersionCheckResult | null {
  const currentVersion = APP_VERSION

  const query = useQuery<VersionCheckResult | null>({
    queryKey: ['platform-config', 'app-versions', APP_KEY, currentVersion],
    queryFn: async () => {
      try {
        const snap = await getDoc(doc(db, PLATFORM_CONFIG, APP_VERSIONS_DOC))
        if (!snap.exists()) {
          logger.i('platform_config/app_versions not found — skipping version check')
          return null
        }
        const data = snap.data() as Record<string, unknown> | undefined
        const appData = data?.[APP_KEY] as Record<string, unknown> | undefined
        if (!appData) {
          logger.i('platform_config/app_versions missing clubmanager field')
          return null
        }

        const latest = (appData['latest'] as string | undefined) ?? currentVersion
        const minRequired = (appData['minRequired'] as string | undefined) ?? '0.0.0'
        const releaseDate = (appData['releaseDate'] as string | undefined) ?? ''

        const hasUpdate = compareVersions(latest, currentVersion) > 0
        const isForced = compareVersions(currentVersion, minRequired) < 0

        logger.i(
          `Version check: current=${currentVersion} latest=${latest} ` +
            `minRequired=${minRequired} hasUpdate=${hasUpdate} isForced=${isForced}`,
        )

        return {
          currentVersion,
          latestVersion: latest,
          minRequired,
          releaseDate,
          hasUpdate,
          isForced,
        }
      } catch (err) {
        // `permission-denied` here is almost always a benign auth-transition:
        // the query refetches on focus / interval / HMR remount, and Firestore
        // can see a moment where the SDK's internal auth token is mid-sync
        // (sign-in just settled / sign-out just fired / dev HMR re-mounted
        // the provider). The rule itself is `request.auth != null`, so a
        // truly authenticated session passes — there's no recoverable action
        // to take. TanStack-Query's retry catches the next attempt cleanly.
        // Demoting to debug keeps the dev console quiet and keeps Sentry
        // free of the noise that triggered M1 in the 2026-05-23 verification.
        if ((err as { code?: string })?.code === 'permission-denied') {
          logger.d('Version check: permission-denied (auth in flight)', err)
        } else {
          logger.e('Version check failed', err)
        }
        return null
      }
    },
    staleTime: REFETCH_INTERVAL_MS,
    refetchInterval: REFETCH_INTERVAL_MS,
    refetchOnWindowFocus: true,
    retry: 1,
  })

  return query.data ?? null
}

/**
 * Compare two version strings of the form "major.minor.patch" with an
 * optional "+build" suffix. Returns negative if `a < b`, zero if equal,
 * positive if `a > b`. Missing parts default to 0.
 */
function compareVersions(a: string, b: string): number {
  const [a0, a1, a2, a3] = parseParts(a)
  const [b0, b1, b2, b3] = parseParts(b)
  if (a0 !== b0) return a0 - b0
  if (a1 !== b1) return a1 - b1
  if (a2 !== b2) return a2 - b2
  return a3 - b3
}

function parseParts(version: string): [number, number, number, number] {
  const [semver = '', buildRaw] = version.split('+')
  const parts = semver.split('.')
  const toInt = (s: string | undefined): number => {
    const n = Number.parseInt(s ?? '0', 10)
    return Number.isFinite(n) ? n : 0
  }
  return [toInt(parts[0]), toInt(parts[1]), toInt(parts[2]), toInt(buildRaw)]
}
